/**
 * سطرِ سیگنال در فید — قرینه‌ی apps/mobile/src/features/signals/SignalCard.tsx.
 *
 * دو حالت داره و تفاوتشون عمدیه:
 *  - دعوتی که گرفتم: **بدونِ** آمارِ جواب‌ها. تصمیمِ تو نباید به جوابِ بقیه
 *    وابسته شه — همون قانونِ FOMO که RLS هم پشتش ایستاده.
 *  - سیگنالی که خودم ساختم: آمار **هست**، چون سازنده تنها کسیه که حقِ دیدنش داره.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import type { Signal } from '@/core/domain/types';
import { kindMeta } from '@/core/domain/signalKinds';
import { t } from '@/core/lib/i18n';
import { signalWhenLabel } from '@/core/lib/dates';
import { formatApprox } from '@/core/lib/approxLabel';
import { Avatar } from '@/ui/kit';
import { CancelledIcon } from '@/ui/icons';
import { RespondMenu } from './RespondMenu';

/**
 * نشانِ «لغو شد» جلوی عنوان — قرینه‌ی `CancelMark` در اپ.
 *
 * فقط روی لغوشده‌ها می‌شینه، یعنی همون‌هایی که مجبوریم نگه داریم. چیزی که
 * حذف شده اصلاً به فید نمی‌رسه (RLS با `deleted_at` برش می‌داره).
 */
function CancelMark({ signal }: { signal: Signal }) {
  if (signal.status !== 'cancelled') return null;
  return <CancelledIcon size={15} className="cancel-mark" />;
}

/** زمانِ برنامه، نه زمانِ ساخت — چیزی که برای تصمیم گرفتن لازمه. */
function whenOf(s: Signal): string {
  if (s.timeMode === 'exact' && s.startsAt) return signalWhenLabel(s);
  if (s.approxLabel) return formatApprox(s.approxLabel);
  return s.timeMode === 'coordinate' ? t('coordinate') : '';
}

export function IncomingSignalCard({ signal, dim }: { signal: Signal; dim?: boolean }) {
  const answered = !!signal.myResponse && signal.myResponse !== 'pending';

  return (
    // نوارِ رنگیِ کنار = «هنوز جواب نداده‌ای». تنها جای کارت که رنگ داره، پس
    // با یه نگاه معلومه کدوم سطر کار می‌خواد. کمرنگ = دیگه کاری ازت نمی‌خواد
    // (جواب دادی یا گذشته) — حذف نمی‌شه چون ممکنه نظرت عوض شه.
    <article className={`signal${dim ? ' dim' : ''}${answered ? '' : ' needs-you'}`}>
      {/* لینک فقط دورِ متنه، نه کلِ کارت: دکمه‌ی جواب داخلِ کارته و لینکِ
          بیرونی هر کلیکِ اون رو هم می‌دزدید. */}
      <Link to={`/signal/${signal.id}`} className="signal-main">
        <Avatar url={signal.creator?.avatarUrl} name={signal.creator?.displayName} />
        <div className="signal-text">
          <h3><CancelMark signal={signal} />{signal.title}</h3>
          <p className="signal-meta">
            <span>{signal.creator?.displayName ?? '…'}</span>
            {signal.placeName ? <span>· {signal.placeName}</span> : null}
          </p>
        </div>
      </Link>
      <div className="signal-side">
        <span className="signal-when">{whenOf(signal)}</span>
        {/* لغو شده یعنی دیگه جوابی نمی‌خواد. سیگنالی که کسی بهش «هستم»
            نگفته اصلاً این‌جا نیست — حذف می‌شه و RLS از فید برش می‌داره. */}
        {signal.status === 'cancelled'
          ? <span className="dim">{t('signalCancelledBadge')}</span>
          : <RespondMenu signal={signal} />}
      </div>
    </article>
  );
}

export function MineSignalCard({ signal }: { signal: Signal }) {
  const c = signal.counts;
  const meta = kindMeta(signal.kind);
  const status = signal.status === 'confirmed' ? t('statusConfirmed')
    : signal.status === 'completed' ? t('statusCompleted')
      : signal.status === 'cancelled' ? t('signalCancelledBadge')
        : signal.status === 'held' ? `⛔ ${t('heldTitle')}` : t('statusActive');

  return (
    <article className="signal mine">
      <Link to={`/signal/${signal.id}`} className="signal-main">
        <div className="signal-text">
          <h3><CancelMark signal={signal} />{signal.title}</h3>
          <p className="signal-meta">
            <span>{meta.badge(signal)}</span>
            <span>· {status}</span>
          </p>
          {c ? (
            <p className="counts">
              <span className="count ok">{t('inCount', { n: c.in })}</span>
              {c.maybe > 0 ? <span className="count">{t('maybeCount', { n: c.maybe })}</span> : null}
              {c.conditional > 0
                ? <span className="count">{t('condCount', { n: c.conditional })}</span> : null}
              {c.pending > 0
                ? <span className="count wait">{t('pendingCount', { n: c.pending })}</span> : null}
            </p>
          ) : null}
        </div>
      </Link>
      <div className="signal-side">
        <span className="signal-when">{whenOf(signal)}</span>
      </div>
    </article>
  );
}
